/**
 * Cross-tab tile-load priority scheduler.
 *
 * Every dashboard tile mounts immediately today (react-grid-layout keeps all
 * tabs mounted to preserve state — inactive tabs are only hidden via CSS
 * display:none, never unmounted), which means every tile's fetch-on-mount
 * effect used to fire in one large burst regardless of what's actually on
 * screen. This module orders WHEN each tile is allowed to mount its real
 * content (and therefore fire its fetch) instead:
 *
 *   Tier 0 — currently visible (in the active tab): granted immediately.
 *   Tier 1 — off-screen, but in the active tab: trickled in, top-to-bottom.
 *   Tier 2 — in an inactive tab: trickled in last.
 *
 * A tile that becomes newly visible (scroll, or its tab becoming active) is
 * promoted to Tier 0 and granted immediately, regardless of its current
 * queue position — see updateTileState.
 *
 * Plain module-level singleton (no React), matching the existing house
 * pattern of cross-cutting coordination state in
 * services/api/client.js's inflightGetRequests/inflightHighPriorityRequests.
 */

const STAGGER_MS = 150;

/** @type {Map<string, { tab: string|undefined, isActiveTab: boolean, isVisible: boolean, gridY: number|undefined, ready: boolean }>} */
const tiles = new Map();
/** @type {Map<string, Set<() => void>>} */
const readyListeners = new Map();
let dispatchTimer = null;

function tierOf(state) {
  if (state.isVisible) return 0;
  if (state.isActiveTab) return 1;
  return 2;
}

function notifyReady(id) {
  const listeners = readyListeners.get(id);
  if (listeners) {
    listeners.forEach((callback) => callback());
  }
}

function dispatchAllVisibleImmediately() {
  tiles.forEach((state, id) => {
    if (!state.ready && tierOf(state) === 0) {
      state.ready = true;
      notifyReady(id);
    }
  });
}

function hasPending() {
  for (const state of tiles.values()) {
    if (!state.ready) return true;
  }
  return false;
}

function dispatchNext() {
  let bestId = null;
  let bestTier = null;
  let bestGridY = null;

  tiles.forEach((state, id) => {
    if (state.ready) return;
    const tier = tierOf(state);
    const gridY = state.gridY ?? Infinity;
    if (bestTier === null || tier < bestTier || (tier === bestTier && gridY < bestGridY)) {
      bestTier = tier;
      bestGridY = gridY;
      bestId = id;
    }
  });

  if (bestId !== null) {
    const state = tiles.get(bestId);
    state.ready = true;
    notifyReady(bestId);
  }

  // A batch of tiles may have become Tier 0 since the last check (e.g. an
  // active-tab switch) — grant those without waiting for their own stagger tick.
  dispatchAllVisibleImmediately();

  if (hasPending()) {
    scheduleDispatch();
  }
}

function scheduleDispatch() {
  if (dispatchTimer) return;
  dispatchTimer = setTimeout(() => {
    dispatchTimer = null;
    dispatchNext();
  }, STAGGER_MS);
}

function kick() {
  dispatchAllVisibleImmediately();
  if (hasPending()) {
    scheduleDispatch();
  }
}

export function registerTile(id, initial = {}) {
  tiles.set(id, {
    tab: initial.tab,
    isActiveTab: !!initial.isActiveTab,
    isVisible: !!initial.isVisible,
    gridY: initial.gridY,
    ready: false,
  });
  kick();
}

export function updateTileState(id, patch) {
  const state = tiles.get(id);
  if (!state) return;
  Object.assign(state, patch);
  kick();
}

export function unregisterTile(id) {
  tiles.delete(id);
  readyListeners.delete(id);
}

export function subscribeReady(id, callback) {
  if (!readyListeners.has(id)) {
    readyListeners.set(id, new Set());
  }
  readyListeners.get(id).add(callback);
  return () => {
    readyListeners.get(id)?.delete(callback);
  };
}

export function isReady(id) {
  return tiles.get(id)?.ready ?? false;
}

// Test-only: clears all module state. Without this, the module singleton
// would leak registered tiles and pending timers across test cases.
export function __resetForTests() {
  tiles.clear();
  readyListeners.clear();
  if (dispatchTimer) {
    clearTimeout(dispatchTimer);
    dispatchTimer = null;
  }
}
